import { useState } from "react";

interface UsePolicyNavigationGuardParams<TDestination> {
  /** Whether there are unsaved category edits worth interrupting for. */
  isDirty: boolean;
  /** Move to the destination, once it is safe to. */
  onApply: (destination: TDestination) => void;
  /** Resolves false when the save failed, which must not navigate. */
  onSave: () => Promise<boolean>;
  onDiscard: () => void;
}

/**
 * Guards a way out of unsaved work so it gets a save/discard prompt first.
 * The destination is whatever the caller needs to remember: a role and policy
 * type inside the transaction policies tab, a tab index on the page above it.
 *
 * Every exit has to route through `request`, which is the point: a caller that
 * navigates directly bypasses the prompt, and that is how edits get lost.
 */
export const usePolicyNavigationGuard = <TDestination>({
  isDirty,
  onApply,
  onSave,
  onDiscard,
}: UsePolicyNavigationGuardParams<TDestination>) => {
  const [pending, setPending] = useState<TDestination | null>(null);
  const [isPrompting, setIsPrompting] = useState(false);

  const request = (destination: TDestination) => {
    if (isDirty) {
      setPending(destination);
      setIsPrompting(true);
      return;
    }
    onApply(destination);
  };

  const applyPending = () => {
    if (pending !== null) {
      onApply(pending);
    }
    setPending(null);
  };

  return {
    isPrompting,
    request,
    // Only a confirmed save navigates. submitUpdatedSettings catches its own
    // failures, so awaiting it is not proof of anything: a failed save used to
    // switch policy anyway, stranding the edits under the wrong one. The prompt
    // stays open so the toast has somewhere to send the user back to.
    save: async () => {
      const saved = await onSave();
      if (!saved) return;
      setIsPrompting(false);
      applyPending();
    },
    discard: () => {
      onDiscard();
      applyPending();
      setIsPrompting(false);
    },
    // Closing means staying: the destination is dropped, so the navigation
    // this prompt interrupted does not happen.
    cancel: () => {
      setPending(null);
      setIsPrompting(false);
    },
  };
};
